import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layout, Menu, Modal, Skeleton } from "antd";
import useUserStore from "../../../store/user/user";
import useAdminDataStore, { selectCurrentGroupId } from "../../../store/adminData/adminData";
import "./SideNav.css";
import { fbLogoutUser } from "../../../services/firebaseService/endPoints/user";
import { useLocation, useNavigate } from "react-router-dom";
import { LogoutIcon } from "../../../utils/icons.jsx";
import useGlobalStore from "../../../store/store";
import { isMobile } from "../../../utils/helper";
import useThemeStore from "../../../store/theme/theme";
import GetStartedWidget from "../../GettingStarted/GetStartedWidget/GetStartedWidget.jsx";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton.jsx";
import {
  adminLinks,
  clubAdminLinks,
  superAdminSideNavLinks,
} from "./SideNavLinks/index.js";
import useDashboardInfo from "../../../hooks/useDashboardInfo.js";
const { Sider } = Layout;

const LogoutButton = ({ expanded, loading, onLogout }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {loading ? (
        expanded ? (
          <Skeleton.Button active style={{ width: "100%", height: 32 }} />
        ) : (
          <Skeleton.Avatar active size="small" shape="circle" style={{ width: 22, height: 22 }} />
        )
      ) : expanded ? (
        <ChantButton
          className="btn-info"
          style={{ background: "transparent", width: "100%", borderColor: "var(--primary-text-color)", color: "var(--primary-text-color)" }}
          onClick={() => setOpen(true)}
        >
          Logout
        </ChantButton>
      ) : (
        <i className="anticon cursor-pointer" onClick={() => setOpen(true)}>
          <img alt="logout-button" src={LogoutIcon} width={22} />
        </i>
      )}

      <Modal open={open} onCancel={() => setOpen(false)} centered width={360} footer={null} closable={false}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "12px 0 4px", gap: "8px" }}>
          <div style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--primary-background)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "4px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--primary-text-color, #fff)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#111", letterSpacing: "-0.02em" }}>Log out?</h2>
          <p style={{ margin: "2px 0 20px", fontSize: 14, color: "#888", textAlign: "center" }}>You'll be returned to the login screen.</p>
          <div style={{ display: "flex", gap: 10, width: "100%" }}>
            <button onClick={() => setOpen(false)} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1.5px solid #e5e5e5", background: "#fafafa", fontSize: 15, fontWeight: 500, color: "#444", cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={() => { setOpen(false); onLogout(); }} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: "var(--primary-background)", fontSize: 15, fontWeight: 600, color: "var(--primary-text-color, #fff)", cursor: "pointer" }}>
              Log out
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const SideNav = ({ expanded, handleSideNavCollapse, loading = false }) => {
  const userRoles = useUserStore((state) => state.user.userRoles);
  const currentGroupId = useAdminDataStore(selectCurrentGroupId);
  const userRole = userRoles?.[currentGroupId] || undefined;
  const navigate = useNavigate();
  const resetAllStores = useGlobalStore((state) => state.resetAllStores);
  const [width, setWidth] = useState(window.innerWidth);
  const location = useLocation();
  const { dashboardType } = useDashboardInfo();

  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(location.pathname);
  const [manuallyToggled, setManuallyToggled] = useState(false);

  const sidenavExpanded = useGlobalStore((state) => state.app.sidenavExpanded);
  const setSidenavExpanded = useGlobalStore(
    (state) => state.app.setSidenavExpanded,
  );

  const handleCollapse = (value) => {
    handleSideNavCollapse(value);
    setSidenavExpanded(!sidenavExpanded);
  };

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
    setManuallyToggled(true);
  };

  const onSelect = ({ key, keyPath }) => {
    setSelectedKey(key);
    if (keyPath.length > 1) {
      setOpenKeys([keyPath[1]]);
    }
  };

  const handleLogout = () => {
    fbLogoutUser().then(() => {
      useThemeStore.getState().resetThemeColors();
      resetAllStores();
      navigate("/login");
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Handle admin group change
    const path = location.pathname.split("/")[1];

    if (
      dashboardType !== "superadmin" &&
      userRole === "super-admin" &&
      path === "superadmin"
    ) {
      useAdminDataStore.getState().setCurrentGroup("", "superadmin");
    }

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardType, userRole, location.pathname]);

  const rootEl = document.querySelector(":root");
  rootEl.style.setProperty("--top-margin", expanded ? "0px" : "-5px");

  const menuItems = useMemo(() => {
    if (
      (userRole === "parentAdmin" || userRole === "admin") &&
      dashboardType === "admin"
    ) {
      return adminLinks;
    } else if (userRole === "parentAdmin" && dashboardType === "clubadmin") {
      return clubAdminLinks;
    } else if (dashboardType === "superadmin") {
      return superAdminSideNavLinks;
    }
    return [];
  }, [userRole, dashboardType]);

  // Extract all menu item keys recursively from menu structure
  const getAllMenuKeys = useMemo(() => {
    const extractKeys = (items) => {
      const keys = [];
      if (!items || !Array.isArray(items)) return keys;

      items.forEach((item) => {
        if (
          item.key &&
          typeof item.key === "string" &&
          item.key.startsWith("/")
        ) {
          keys.push(item.key);
        }
        if (item.children) {
          keys.push(...extractKeys(item.children));
        }
      });
      return keys;
    };

    return extractKeys(menuItems);
  }, [menuItems]);

  const subMenuParentMap = useMemo(() => {
    return {
      "/admin/members": "sub1",
      "/admin/members/all": "sub1",
      "/admin/members/leaderboard": "sub1",
      "/admin/members/purchaseReport": "sub1",
      "/admin/members/packages": "sub1",
      "/admin/members/onboard": "sub1",
      "/admin/members/safety": "sub1",
      "/admin/members/promote": "sub1",
      "/admin/membershipcard": "sub1",

      "/admin/content": "sub2",
      "/admin/content/polls": "sub2",
      "/admin/content/giveaways": "sub2",
      "/admin/content/locations": "sub2",
      "/admin/welcome": "sub2",
      "/admin/gallery": "sub2",
      "/admin/partners": "sub2",
      "/admin/partners/list": "sub2",
      "/admin/chants": "sub2",

      "/admin/store": "sub3",
      "/admin/store/orders": "sub3",
      "/admin/store/merch": "sub3",
      "/admin/store/settings": "sub3",

      "/admin/tickets": "sub4",
      "/admin/tickets/all": "sub4",
      "/admin/tickets/update": "sub4",

      "/admin/website": "sub5",
      "/admin/website/general": "sub5",
      "/admin/website/sections": "sub5",

      "/admin/stadium": "sub6",
      "/admin/stadium/setup": "sub6",

      "/admin/directory": "sub7",
      "/admin/directory/groupListing": "sub7",

      "/admin/help": null,

      "/admin/groupSettings": "sub8",
      "/admin/appSettings": "sub8",
      "/admin/paymentDetails": "sub8",

      // Superadmin
      "/superadmin/groups": "/superadmin/groups",
      "/superadmin/adoption": "/superadmin/adoption",
      "/superadmin/missing-transactions": "/superadmin/missing-transactions",
      "/superadmin/directory": "/superadmin/directory",
      "/superadmin/polls": "/superadmin/polls",
      "/superadmin/report/transactions": "/superadmin/report/transactions",
      "/superadmin/status/report": "/superadmin/status/report",
      "/superadmin/deactivated": "/superadmin/deactivated",
      "/superadmin/data": "/superadmin/data",
      "/superadmin/design": "/superadmin/design",
      "/superadmin/packages": "/superadmin/packages",
      "/superadmin/alerts": "/superadmin/alerts",
    };
  }, []);

  const [currentPath, setCurrentPath] = useState(location.pathname);
  const prevPathRef = useRef();

  useEffect(() => {
    const locationPath = location.pathname;

    // Find the longest matching menu item key that the current path starts with
    const findMatchingMenuKey = (path) => {
      // First try exact match
      if (getAllMenuKeys.includes(path)) {
        return path;
      }

      // Then find the longest prefix match
      let bestMatch = null;
      let bestMatchLength = 0;

      getAllMenuKeys.forEach((menuKey) => {
        if (path.startsWith(menuKey) && menuKey.length > bestMatchLength) {
          // Ensure we match at a path boundary (not in the middle of a word)
          const nextChar = path[menuKey.length];
          if (!nextChar || nextChar === "/") {
            bestMatch = menuKey;
            bestMatchLength = menuKey.length;
          }
        }
      });

      return bestMatch || path;
    };

    const matchingMenuKey = findMatchingMenuKey(locationPath);

    if (locationPath !== currentPath) {
      setCurrentPath(locationPath);
      setSelectedKey(matchingMenuKey);
    } else if (selectedKey !== matchingMenuKey) {
      setSelectedKey(matchingMenuKey);
    }

    // Find parent key for opening submenu
    const path = locationPath.split("/");
    const basePath =
      path.length > 2 && path[1] && path[2]
        ? `/${path[1]}/${path[2]}`
        : locationPath;

    // Try to find parent key from matching menu key first, then fallback to locationPath/basePath
    const menuKeyForParent =
      matchingMenuKey !== locationPath ? matchingMenuKey : locationPath;

    // Helper to find parent key by checking the key and its prefixes
    const findParentKey = (key) => {
      if (subMenuParentMap[key]) {
        return subMenuParentMap[key];
      }
      // Try to find parent by checking prefixes (e.g., /admin/content/giveaways -> /admin/content -> sub2)
      const keyParts = key.split("/").filter(Boolean);
      for (let i = keyParts.length - 1; i >= 1; i--) {
        const prefix = "/" + keyParts.slice(0, i).join("/");
        if (subMenuParentMap[prefix]) {
          return subMenuParentMap[prefix];
        }
      }
      return null;
    };

    const parentKey =
      findParentKey(menuKeyForParent) ||
      findParentKey(locationPath) ||
      findParentKey(basePath);

    if (
      prevPathRef.current !== locationPath &&
      parentKey &&
      openKeys[0] !== parentKey &&
      !manuallyToggled
    ) {
      setOpenKeys([parentKey]);
      setManuallyToggled(false);
    }

    if (manuallyToggled) {
      setManuallyToggled(false);
    }

    prevPathRef.current = locationPath;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    openKeys,
    manuallyToggled,
    subMenuParentMap,
    expanded,
    getAllMenuKeys,
    menuItems,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Skeleton menu items
  const renderSkeletonMenu = () => (
    <div className="sidenav-skeleton-container">
      {[...Array(expanded ? 8 : 6)].map((_, index) => (
        <div key={index} className="sidenav-skeleton-item">
          {expanded ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 24px",
              }}
            >
              <Skeleton.Avatar
                active
                size="small"
                shape="square"
                style={{ width: 20, height: 20 }}
              />
              <Skeleton.Input
                active
                size="small"
                style={{ width: 120, height: 16 }}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px",
              }}
            >
              <Skeleton.Avatar
                active
                size="small"
                shape="square"
                style={{ width: 20, height: 20 }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Sider
      collapsible
      collapsed={!expanded}
      onCollapse={handleCollapse}
      collapsedWidth={width > 500 ? "80" : "0"}
      width={215}
      style={{
        backgroundColor: loading ? "#f5f5f5" : "var(--primary-background)",
        position: "fixed",
        overflow: "auto",
        height: "100%",
        zIndex: "100",
        boxShadow: "0px 1px 3px 0px",
        "--sidenav-trigger-bg-color": loading
          ? "#f5f5f5"
          : "var(--primary-background)",
        "--sidenav-submenu-item-margin": expanded ? "8px" : "0px",
        filter: loading ? "grayscale(0.3) opacity(0.9)" : "none",
      }}
      className={`side-nav ${loading ? "side-nav-loading" : ""}`}
    >
      <div className="demo-logo-vertical" />

      {loading ? (
        renderSkeletonMenu()
      ) : (
        <Menu
          theme="dark"
          mode="inline"
          items={menuItems}
          defaultSelectedKeys={[location.pathname]}
          // selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          onSelect={onSelect}
          selectedKeys={[selectedKey]}
          onClick={() => isMobile() && handleSideNavCollapse(true)}
        />
      )}

      <div
        className="sidenav-logout-btn"
        style={{
          "--logout-btn-bg-color": "var(--primary-background)",
          "--logout-btn-text-color": "var(--primary-text-color)",
        }}
      >
        <LogoutButton expanded={expanded} loading={loading} onLogout={handleLogout} />
      </div>

      {!isMobile() && userRole === "admin" && dashboardType === "admin" && (
        <div className="sidenav-footer">
          {loading ? (
            expanded && (
              <Skeleton
                active
                paragraph={{ rows: 2 }}
                style={{ padding: "12px" }}
              />
            )
          ) : (
            <GetStartedWidget sidenavExpanded={expanded} />
          )}
        </div>
      )}
    </Sider>
  );
};

export default SideNav;
